import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import {ErrorHandlerService} from "../error-handler.service";
import {environment} from "../../../../environments/environment";

// Интерфейсы для типизации
export interface AccessKey {
  id: string;
  title: string;
  value: string;
  createdAt: string;
  mode: 'live' | 'test' | 'sandbox';
  type: 'public' | 'api' | 'dashboard';
  endpoints?: string[];
  view?: string[];
  role?: 'master' | 'standard' | 'sub_key';  // Роль ключа
  is_blocked?: boolean;                       // Заблокирован ли ключ
  master_key_id?: string;                     // ID мастер-ключа (для саб-ключей)
  subKeys?: AccessKey[];                      // Массив дочерних ключей (для мастер-ключей)
}

export interface SdkCost {
  title: string;
  cost: number;
}

export interface CreateAccessKeyRequest {
  title: string;
  mode: 'live' | 'test' | 'sandbox';
  type: 'public' | 'api';
  endpoints?: string[];
  view?: string[];
}

export interface CreateSubKeyRequest {
  master_key: string;  // UUID или значение мастер-ключа
  title: string;
  endpoints?: string[];
}

export interface EditAccessKeyRequest {
  title?: string;
  endpoints?: string[];
  view?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AccessKeyService {
  private _apiUrl: string = `${environment.apiUrl}/api`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService,
  ) {}

  getSdkCosts(projectId: string): Observable<SdkCost[]> {
    return this.http.get<SdkCost[]>(`${this._apiUrl}/${projectId}/access-key/sdk-cost`).pipe(
      map(response => response || []),
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }

  getAccessKeys(projectId: string): Observable<AccessKey[]> {
    return this.http.get<any[]>(`${this._apiUrl}/${projectId}/access-key/list`).pipe(
      map(keys => keys.map(key => ({
        ...key,
        // Маппим роль из верхнего регистра в нижний
        role: this.mapRole(key.role)
      }))),
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Маппинг роли из формата бэкенда в формат фронтенда
   */
  private mapRole(role: string | undefined): 'master' | 'standard' | 'sub_key' | undefined {
    if (!role) return undefined;
    
    switch (role.toUpperCase()) {
      case 'MASTER':
        return 'master';
      case 'SUB_KEY':
        return 'sub_key';
      case 'STANDARD':
        return 'standard';
      default:
        return 'standard'; // По умолчанию считаем стандартным
    }
  }

  createAccessKey(projectId: string, request: CreateAccessKeyRequest): Observable<any> {
    return this.http.post<any>(`${this._apiUrl}/${projectId}/access-key/create`, request).pipe(
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }

  editAccessKey(projectId: string, keyId: string, request: EditAccessKeyRequest): Observable<any[]> {
    return this.http.post<any[]>(`${this._apiUrl}/${projectId}/access-key/edit/${keyId}`, request).pipe(
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }

  deleteAccessKey(projectId: string, keyId: string): Observable<any[]> {
    return this.http.post<any[]>(`${this._apiUrl}/${projectId}/access-key/delete/${keyId}`, {}).pipe(
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }

  // Новые методы для работы с мастер-ключами и саб-ключами

  /**
   * Сделать ключ мастер-ключом (разрешить создание саб-ключей)
   */
  allowMaster(projectId: string, keyId: string): Observable<any> {
    return this.http.post<any>(`${this._apiUrl}/admin/keys/${keyId}/allow-master`, {}).pipe(
      map(response => {
        // Маппим роль если есть
        if (response && response.role) {
          response.role = this.mapRole(response.role);
        }
        return response;
      }),
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Отозвать права мастер-ключа
   */
  revokeMaster(projectId: string, keyId: string): Observable<any> {
    return this.http.post<any>(`${this._apiUrl}/admin/keys/${keyId}/revoke-master`, {}).pipe(
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Заблокировать ключ
   */
  blockKey(projectId: string, keyId: string): Observable<any> {
    return this.http.post<any>(`${this._apiUrl}/admin/keys/${keyId}/block`, {}).pipe(
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Разблокировать ключ
   */
  unblockKey(projectId: string, keyId: string): Observable<any> {
    return this.http.post<any>(`${this._apiUrl}/admin/keys/${keyId}/unblock`, {}).pipe(
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }

  /**
   * Создать саб-ключ через endpoint (by-master)
   * @param request - данные для создания саб-ключа (включая master_key)
   */
  createSubKey(request: CreateSubKeyRequest): Observable<AccessKey> {
    return this.http.post<any>(`${this._apiUrl}/me/sub-keys/by-master`, request).pipe(
      map(response => {
        const subKey = response.sub_key;
        // Преобразуем created_at в createdAt если нужно
        if (subKey && subKey.created_at && !subKey.createdAt) {
          subKey.createdAt = subKey.created_at;
        }
        // Маппим роль
        if (subKey && subKey.role) {
          subKey.role = this.mapRole(subKey.role);
        }
        return subKey;
      }),
      catchError((err) => {
        this.errorHandler.handleError(err);
        return throwError(() => err);
      }),
    );
  }
}
